import { GameState, PieceType } from '../../types';
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from '../Core/IAIPlayer';
import { selectAstraPiece } from './AstraDraft';
import { AstraSearch } from './AstraSearch';
import { AstraRules, copyState, describeMove } from './AstraRules';
import { ASTRA_VERSION, evaluate, MATE } from './AstraEvaluator';
import { ZodiacFEN } from '../../Fen/ZodiacFEN';
import type { ZodiacEngine } from '../../zodiacEngine';

export class AstraAI implements IAIPlayer {
  readonly name=AIPlayerType.ZSTR;
  readonly displayName='Zodiac Astra';
  readonly description='ZSTR: strategic alpha-beta search using production Zodiac rules';
  private search=new AstraSearch();
  private stats:AIStats={movesCalculated:0,averageThinkingTime:0,nodesEvaluated:0};
  constructor(private readonly gameEngine?: ZodiacEngine) {}

  selectKing(kings:PieceType[]):PieceType {
    // Same random king policy as MM: first-round results should measure play, not king selection.
    return kings[Math.floor(Math.random()*kings.length)];
  }
  selectPiece(available:PieceType[],opponent:PieceType[],own:PieceType[]):PieceType {
    return selectAstraPiece(available,opponent,own);
  }
  async selectMove(state:GameState,maxThinkingTime:number):Promise<AIMoveResult> {
    const searchState={...state,astraPendingFens:this.gameEngine?.getPendingTurnFENs() || []};
    const result=this.search.findBestMove(searchState,maxThinkingTime);
    const piece=state.board[result.move.fromPosition.row][result.move.fromPosition.col].Pieces.find(p=>p.id===result.move.move.pieceId);
    this.stats.averageThinkingTime=(this.stats.averageThinkingTime*this.stats.movesCalculated+result.elapsedMs)/(this.stats.movesCalculated+1);
    this.stats.movesCalculated++;
    this.stats.nodesEvaluated=(this.stats.nodesEvaluated||0)+result.nodes+result.qnodes;
    const rules=new AstraRules();let position=copyState(searchState);
    const pv:any[]=[];
    let predictedAfterFens:string[]=[];
    // PV follows one possible dice outcome, not a promise of a forced sequence.
    for(const move of result.pv.slice(0,16)) {
      const description=describeMove(position,move);
      const outcomes=rules.outcomes(position,move);
      if(!outcomes.length) break;
      if(pv.length===0) predictedAfterFens=outcomes.map(o=>ZodiacFEN.encode(o.state));
      const next=outcomes.reduce((a,b)=>a.probability>=b.probability?a:b).state;
      pv.push({fromPosition:move.fromPosition,move:move.move,description,score:result.score,
        depth:result.depth,playerTurn:position.isLightTurn?'Light':'Dark',fen:ZodiacFEN.encode(next)});
      position=next;
    }
    // The UI executes by object identity. Return references from the real input,
    // never the search clone (especially captured pieces and side effects).
    const livePieces=new Map(state.board.flatMap(r=>r.flatMap(s=>s.Pieces.map(p=>[p.id,p] as const))));
    const liveMove={...result.move.move,toPiece:result.move.move.toPiece?livePieces.get(result.move.move.toPiece.id)||null:null,
      sideEffects:result.move.move.sideEffects.map(se=>({...se,piecesToEffect:se.piecesToEffect.map(p=>livePieces.get(p.id)||p)}))};
    return {fromPosition:result.move.fromPosition,move:liveMove,thinkingTime:result.elapsedMs,
      confidence:Math.abs(result.score)>MATE-100?0.95:Math.min(0.8,0.4+result.depth*0.07),
      evaluation:result.score,reasoning:`${ASTRA_VERSION}: depth ${result.depth}, ${result.nodes+result.qnodes} nodes; ${describeMove(state,result.move)}`,
      movingPiece:piece?{type:piece.type,isKing:piece.isKing}:undefined,
      principalVariation:pv,rootMoveScores:result.rootScores,
      evaluationBreakdown:{astra:{version:ASTRA_VERSION,budgetMs:maxThinkingTime,depth:result.depth,nodes:result.nodes,
        qnodes:result.qnodes,cutoffs:result.cutoffs,ttHits:result.ttHits,timedOut:result.timedOut,
        iterations:result.iterations,rootScores:result.rootScores,before:evaluate(state),
        predictedAfterFen:pv[0]?.fen,predictedAfterFens,principalVariation:pv.map(p=>({description:p.description,fen:p.fen})),
        caveats:['Scores are search estimates, not calibrated win probabilities.','PV shows one dice outcome; chance moves use expected values.']}}};
  }
  reset():void {this.stats={movesCalculated:0,averageThinkingTime:0,nodesEvaluated:0};}
  getStats():AIStats {return {...this.stats};}
  configure(config:Record<string,any>):void {
    if(Number.isFinite(config.maxDepth)) this.search=new AstraSearch({maxDepth:Math.max(1,Math.min(16,Math.floor(config.maxDepth)))});
  }
}
